import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { userPreferenceAPI } from '@atlassian/jira-user-preferences-services/src/utils/index.tsx';
import { createResource, useResource } from '@atlassian/react-resource-router';
import { STORE_KEY, RESOURCE_TYPE, RESOURCE_KEY, PACKAGE_NAME } from './constants.tsx';

import type {
	GICSmartFieldsFeatureResourceData,
	GICSmartFieldsMyPreferencesResponse,
} from './types.tsx';

export const gicSmartFieldsFeatureResource = createResource<GICSmartFieldsFeatureResourceData>({
	type: RESOURCE_TYPE,
	getKey: () => RESOURCE_KEY,
	getData: async (): Promise<GICSmartFieldsFeatureResourceData> => {
		try {
			const data = await userPreferenceAPI.get<GICSmartFieldsMyPreferencesResponse>(STORE_KEY);

			if (!data)
				return {
					hasUserOptedOut: false,
					hasUserDismissedReadMore: false,
					hasUserDismissedGicOnboarding: fg('smart_fields_gic_toggle_fg') ? false : undefined,
				};

			return data;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			// If value has never been set, we expect a 404
			if (error.statusCode === 404) {
				return {
					hasUserOptedOut: false,
					hasUserDismissedReadMore: false,
					hasUserDismissedGicOnboarding: fg('smart_fields_gic_toggle_fg') ? false : undefined,
				};
			}

			fireErrorAnalytics({
				error,
				meta: {
					id: 'gicSmartFieldsFeatureResource',
					packageName: PACKAGE_NAME,
					teamName: 'gryffindor',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		return {
			hasUserOptedOut: false,
			hasUserDismissedReadMore: false,
			hasUserDismissedGicOnboarding: fg('smart_fields_gic_toggle_fg') ? false : undefined,
		};
	},
	maxAge: Number.MAX_VALUE,
});

export const useIsSmartFieldsOptIn = () => {
	const { data, update, loading } = useResource(gicSmartFieldsFeatureResource);

	const currentOptInStatus =
		typeof data?.hasUserOptedOut === 'boolean' ? !data.hasUserOptedOut : true;

	const currentDismissedReadMore =
		typeof data?.hasUserDismissedReadMore === 'boolean' ? data.hasUserDismissedReadMore : false;

	// eslint-disable-next-line no-nested-ternary
	const currentDismissedGicOnboarding = fg('smart_fields_gic_toggle_fg')
		? typeof data?.hasUserDismissedGicOnboarding === 'boolean'
			? data.hasUserDismissedGicOnboarding
			: false
		: undefined;

	const dismissReadMore = useCallback(() => {
		update((previousData) => ({
			...(previousData?.hasUserOptedOut
				? { hasUserOptedOut: previousData.hasUserOptedOut }
				: { hasUserOptedOut: false }),
			hasUserDismissedReadMore: true,
			// eslint-disable-next-line no-nested-ternary
			...(fg('smart_fields_gic_toggle_fg')
				? previousData?.hasUserDismissedGicOnboarding
					? { hasUserDismissedGicOnboarding: previousData.hasUserDismissedGicOnboarding }
					: { hasUserDismissedGicOnboarding: false }
				: {}),
		}));
	}, [update]);

	const dismissGicOnboarding = useCallback(() => {
		update((previousData) => ({
			...(previousData?.hasUserOptedOut
				? { hasUserOptedOut: previousData.hasUserOptedOut }
				: { hasUserOptedOut: false }),
			...(previousData?.hasUserDismissedReadMore
				? { hasUserDismissedReadMore: previousData.hasUserDismissedReadMore }
				: { hasUserDismissedReadMore: false }),
			hasUserDismissedGicOnboarding: true,
		}));
	}, [update]);

	return {
		isSmartFieldsOptIn: loading ? null : currentOptInStatus,
		isSmartFieldDismissedReadMore: loading ? null : currentDismissedReadMore,
		isSmartFieldsGicOnboardingDismissed: loading ? null : currentDismissedGicOnboarding,
		dismissReadMore,
		dismissGicOnboarding,
		loading,
	};
};
